import {
  endOfDay, startOfDay, addWeeks,
} from 'date-fns';
import { InvoiceStatus, InvoiceType } from '../../../domain/types';
import { FiltersMapper } from '../../../hooks/use-filters/useFilters';
import { InvoiceSerpPageFilterOption, InvoiceSerpPageFilters, InvoiceSerpSortOption } from './types';
import { SortOrder } from '../../../providers/pagination';

export const LOAD_SERP_INVOICES_ERROR_MESSAGE = 'Non è stato possibile recuperare i pagamenti.';
export const NO_SERP_INVOICES = 'Non sono presenti pagamenti.';

export const INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION: InvoiceSerpPageFilterOption = {
  key: 'all',
  label: {
    short: 'Tutti',
    long: 'Tutti i pagamenti',
  },
  value: {},
};

export const INVOICE_SERP_PAGE_STATUS_FILTERS: InvoiceSerpPageFilterOption[] = [
  {
    key: 'draft',
    label: {
      short: 'Richiesti',
      long: 'Pagamenti richiesti',
    },
    value: {
      status: InvoiceStatus.DRAFT,
    },
  },
  {
    key: 'proforma',
    label: {
      short: 'Emessi',
      long: 'Pagamenti emessi',
    },
    value: {
      status: InvoiceStatus.PROFORMA,
    },
  },
  {
    key: 'billed',
    label: {
      short: 'Fatturati',
      long: 'Pagamenti fatturati',
    },
    value: {
      status: InvoiceStatus.BILLED,
    },
  },
  {
    key: 'paid',
    label: {
      short: 'Registrati',
      long: 'Pagamenti registrati',
    },
    value: {
      status: InvoiceStatus.PAID,
    },
  },
  {
    key: 'notPaid',
    label: {
      short: 'Non pagati',
      long: 'Pagamenti non pagati',
    },
    value: {
      status: InvoiceStatus.NOT_PAID,
    },
  },
  {
    key: 'collected',
    label: {
      short: 'Incassati',
      long: 'Pagamenti incassati',
    },
    value: {
      status: InvoiceStatus.COLLECTED,
    },
  },
];

export const INVOICE_SERP_PAGE_TYPE_FILTERS: InvoiceSerpPageFilterOption[] = [
  {
    key: 'mediation',
    label: {
      short: 'Mediazione',
      long: 'Mediazione',
    },
    value: {
      type: InvoiceType.MEDIATION,
    },
  },
  {
    key: 'extraPackage',
    label: {
      short: 'Pacchetti extra',
      long: 'Pacchetti extra',
    },
    value: {
      type: InvoiceType.EXTRA_PACKAGE,
    },
  },
  {
    key: 'creditNote',
    label: {
      short: 'Nota di credito',
      long: 'Nota di credito',
    },
    value: {
      type: InvoiceType.CREDIT_NOTE,
    },
  },
  {
    key: 'penalty',
    label: {
      short: 'Penale',
      long: 'Penale',
    },
    value: {
      type: InvoiceType.PENALTY,
    },
  },
];

export const INVOICE_SERP_PAGE_EXPIRE_FILTERS: InvoiceSerpPageFilterOption[] = [
  {
    key: 'expiredPayment',
    label: {
      short: 'Scaduti',
      long: 'Scaduti',
    },
    value: {
      status: [InvoiceStatus.DRAFT, InvoiceStatus.PROFORMA],
      paymentDateLt: startOfDay(new Date()),
    },
  },
  {
    key: 'expiringPayment',
    label: {
      short: 'In scadenza',
      long: 'In scadenza',
    },
    value: {
      status: [InvoiceStatus.DRAFT, InvoiceStatus.PROFORMA],
      paymentDateGt: startOfDay(new Date()),
      paymentDateLt: addWeeks(endOfDay(new Date()), 1),
    },
  },
];

export const INVOICE_SERP_PAGE_FILTER_OPTIONS: InvoiceSerpPageFilterOption[] = [
  ...INVOICE_SERP_PAGE_STATUS_FILTERS,
  ...INVOICE_SERP_PAGE_TYPE_FILTERS,
  ...INVOICE_SERP_PAGE_EXPIRE_FILTERS,
  INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION,
];

export const INVOICE_SERP_PAGE_GROUPED_FILTER_OPTIONS = [
  {
    label: 'Stato',
    options: INVOICE_SERP_PAGE_STATUS_FILTERS,
  },
  {
    label: 'Tipologia',
    options: INVOICE_SERP_PAGE_TYPE_FILTERS,
  },
  {
    label: 'Scadenza',
    options: INVOICE_SERP_PAGE_EXPIRE_FILTERS,
  },
];

export const INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'default',
  label: 'In evidenza',
  value: {
    createdAt: SortOrder.DESC,
  },
} as const;

export const INVOICE_SERP_PAGE_CREATED_AT_ASC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'createdAtAsc',
  label: 'Creazione meno recente',
  value: {
    createdAt: SortOrder.ASC,
  },
} as const;

export const INVOICE_SERP_PAGE_CREATED_AT_DESC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'createdAtDesc',
  label: 'Creazione più recente',
  value: {
    createdAt: SortOrder.DESC,
  },
} as const;

export const INVOICE_SERP_PAGE_UPDATED_AT_ASC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'updatedAtAsc',
  label: 'Aggiornamento meno recente',
  value: {
    updatedAt: SortOrder.ASC,
  },
} as const;

export const INVOICE_SERP_PAGE_UPDATED_AT_DESC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'updatedAtDesc',
  label: 'Aggiornamento più recente',
  value: {
    updatedAt: SortOrder.DESC,
  },
} as const;

export const INVOICE_SERP_PAGE_PAYMENT_DATE_ASC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'paymentDateAsc',
  label: 'Pagamento meno recente',
  value: {
    paymentDate: SortOrder.ASC,
  },
} as const;

export const INVOICE_SERP_PAGE_PAYMENT_DATE_DESC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'paymentDateDesc',
  label: 'Pagamento più recente',
  value: {
    paymentDate: SortOrder.DESC,
  },
} as const;

export const INVOICE_SERP_PAGE_VALUE_ASC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'valueAsc',
  label: 'Importo crescente',
  value: {
    value: SortOrder.ASC,
  },
} as const;

export const INVOICE_SERP_PAGE_VALUE_DESC_SORT_OPTION: InvoiceSerpSortOption = {
  key: 'valueDesc',
  label: 'Importo decrescente',
  value: {
    value: SortOrder.DESC,
  },
} as const;

export const INVOICE_SERP_PAGE_SORT_OPTIONS: InvoiceSerpSortOption[] = [
  INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION,
  INVOICE_SERP_PAGE_CREATED_AT_DESC_SORT_OPTION,
  INVOICE_SERP_PAGE_CREATED_AT_ASC_SORT_OPTION,
  INVOICE_SERP_PAGE_UPDATED_AT_DESC_SORT_OPTION,
  INVOICE_SERP_PAGE_UPDATED_AT_ASC_SORT_OPTION,
  INVOICE_SERP_PAGE_PAYMENT_DATE_DESC_SORT_OPTION,
  INVOICE_SERP_PAGE_PAYMENT_DATE_ASC_SORT_OPTION,
  INVOICE_SERP_PAGE_VALUE_DESC_SORT_OPTION,
  INVOICE_SERP_PAGE_VALUE_ASC_SORT_OPTION,
];

export const INVOICE_SERP_PAGE_FILTERS_MAPPER: FiltersMapper<InvoiceSerpPageFilters> = {
  q: (v) => v,
  filter: (v) => INVOICE_SERP_PAGE_FILTER_OPTIONS.find(({
    key,
  }) => key === v)?.key || INVOICE_SERP_PAGE_ALL_INVOICES_FILTER_OPTION.key,
  sort: (v) => INVOICE_SERP_PAGE_SORT_OPTIONS.find(({
    key,
  }) => key === v)?.key || INVOICE_SERP_PAGE_DEFAULT_SORT_OPTION.key,
};
